if (!customElements.get("product-form")) {
  customElements.define(
    "product-form",
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector("form");
        this.variantIdInput.disabled = false;
        this.form.addEventListener("submit", this.onSubmitHandler.bind(this));
        this.cart =
          document.querySelector("cart-notification") ||
          document.querySelector("cart-drawer");
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton
          ? this.submitButton.querySelector("span")
          : null;

        this.loadingSpinner = this.querySelector(".loading__spinner");

        if (document.querySelector("cart-drawer"))
          this.submitButton.setAttribute("aria-haspopup", "dialog");

        this.hideErrors = this.dataset.hideErrors === "true";
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute("aria-disabled") === "true") return;

        this.handleErrorMessage();

        this.submitButton.setAttribute("aria-disabled", true);
        this.submitButton.classList.add("loading");
        if (this.loadingSpinner) this.loadingSpinner.classList.remove("hidden");

        const config = fetchConfig("javascript");
        config.headers["X-Requested-With"] = "XMLHttpRequest";
        delete config.headers["Content-Type"];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            "sections",
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append("sections_url", window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        // Ensure we always hit the JSON endpoint so errors are returned as JSONd
        // (Shopify may return an HTML error page for /cart/add).
        const cartAddUrl = (() => {
          const raw = String(routes?.cart_add_url || "/cart/add.js");
          if (/\.js(\?|#|$)/.test(raw)) return raw;
          if (/\?|#/.test(raw)) {
            const [path, rest] = raw.split(/(?=[?#])/);
            return `${path}.js${rest || ""}`;
          }
          return `${raw}.js`;
        })();

        fetch(cartAddUrl, config)
          .then(async (fetchResponse) => {
            // Shopify may return JSON *or* HTML depending on endpoint / config.
            // Make sure we surface something useful in console either way.
            let parsed;
            try {
              parsed = await fetchResponse.json();
            } catch (jsonErr) {
              const bodyText = await fetchResponse.text().catch(() => "");
              const err = new Error(
                `Cart add returned non-JSON (HTTP ${fetchResponse.status})`
              );
              err.name = "CartAddNonJsonError";
              err.status = fetchResponse.status;
              err.url = fetchResponse.url;
              err.bodyText = bodyText;
              throw err;
            }

            // Attach HTTP status so we can handle non-2xx even if JSON doesn't include `status`.
            parsed.__httpStatus = fetchResponse.status;
            return parsed;
          })
          .then((response) => {
            const httpFailed =
              typeof response?.__httpStatus === "number" &&
              response.__httpStatus >= 400;

            if (response.status || httpFailed) {
              // Debug: log full server response for diagnosing add-to-cart failures
              console.log("Cart add failed response:", response);

              publish(PUB_SUB_EVENTS.cartError, {
                source: "product-form",
                productVariantId: formData.get("id"),
                errors: response.errors || response.description,
                message: response.message,
              });

              // Provide a clearer, actionable error for sold-out responses
              const serverDesc =
                response.description ||
                response.errors ||
                response.message ||
                "";
              const isSoldOutError = /sold out/i.test(String(serverDesc));
              if (isSoldOutError) {
                const guidance =
                  "This variant is marked sold out in Shopify. To allow purchases when out of stock, set the variant's Inventory policy to 'Continue selling when out of stock' in Shopify Admin (or disable inventory tracking).";
                this.handleErrorMessage(guidance);
              } else {
                this.handleErrorMessage(serverDesc);
              }

              const soldOutMessage =
                this.submitButton.querySelector(".sold-out-message");
              if (!soldOutMessage) return;
              this.submitButton.setAttribute("aria-disabled", true);
              if (this.submitButtonText)
                this.submitButtonText.classList.add("hidden");
              // Show the existing sold-out label but also update its text for clarity
              if (isSoldOutError) {
                soldOutMessage.textContent =
                  "Sold out — enable backorders in Shopify Admin to allow purchases.";
              }
              soldOutMessage.classList.remove("hidden");
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker(
              "add:wait-for-subscribers"
            );
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: "product-form",
                productVariantId: formData.get("id"),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker(
                  "add:wait-for-subscribers",
                  startMarker
                );
              });
            this.error = false;
            const quickAddModal = this.closest("quick-add-modal");
            if (quickAddModal) {
              document.body.addEventListener(
                "modalClosed",
                () => {
                  setTimeout(() => {
                    CartPerformance.measure(
                      "add:paint-updated-sections",
                      () => {
                        this.cart.renderContents(response);
                      }
                    );
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            // If Shopify responded with HTML (common on /cart/add), log a snippet.
            if (e && e.name === "CartAddNonJsonError") {
              console.error(e);
              const snippet = String(e.bodyText || "").slice(0, 500);
              if (snippet) {
                console.error(
                  "Cart add non-JSON body (first 500 chars):",
                  snippet
                );
              }
              this.handleErrorMessage(
                "Add to cart failed. Check Console for server response details."
              );
              this.error = true;
              return;
            }

            console.error(e);
            this.handleErrorMessage(
              "Add to cart failed. Check Console for details."
            );
            this.error = true;
          })
          .finally(() => {
            this.submitButton.classList.remove("loading");
            if (this.cart && this.cart.classList.contains("is-empty"))
              this.cart.classList.remove("is-empty");
            if (!this.error) this.submitButton.removeAttribute("aria-disabled");
            if (this.loadingSpinner)
              this.loadingSpinner.classList.add("hidden");

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper ||
          this.querySelector(".product-form__error-message-wrapper");
        if (!this.errorMessageWrapper) return;
        this.errorMessage =
          this.errorMessage ||
          this.errorMessageWrapper.querySelector(
            ".product-form__error-message"
          );

        this.errorMessageWrapper.toggleAttribute("hidden", !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (!this.submitButton) return;

        if (disable) {
          this.submitButton.setAttribute("disabled", "disabled");
          if (text) {
            if (this.submitButtonText) this.submitButtonText.textContent = text;
            else this.submitButton.textContent = text;
          }
        } else {
          this.submitButton.removeAttribute("disabled");
          const label = text || window.variantStrings.addToCart;
          if (this.submitButtonText) this.submitButtonText.textContent = label;
          else this.submitButton.textContent = label;
        }
      }

      get variantIdInput() {
        return this.form.querySelector("[name=id]");
      }
    }
  );
}
